import React, { useState, useEffect } from "react";
import {
  Input,
  Select,
  Form,
  Button
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";


/**
 * addNextKey(pattern, obj)
 * 
 * Takes a pattern string (e.g., "key#") that includes a '#' placeholder for digits
 * and an object whose keys may or may not match that pattern.
 * It finds the highest number in the keys that match the pattern, increments it,
 * and adds a new key with that next number. The new key is set to an empty string.
 * Returns a new object containing all original entries plus the new key.
 *
 * @param {string} pattern - The pattern string with a '#' placeholder for digits.
 * @param {Object} obj - The object to search through and update.
 * @returns {Object} - A new object with the newly added key-value pair.
 */
function addNextKey(pattern, obj) {
  // Create a regex from the pattern by replacing '#' with a capturing group for digits
  const regex = new RegExp("^" + pattern.replace('#', '(\\d+)') + "$");
  // Find the maximum number present in keys matching the pattern
  let max = 0;
  for (const key in obj) {
    const match = key.match(regex);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > max) {
        max = num;
      }
    }
  }
  // Generate the new key by replacing '#' in the pattern with the next number
  const newKey = pattern.replace('#', max + 1);
  // Return a new object containing all original entries and the new key with an empty string
  return { ...obj, [newKey]: "" };
}

/**
 * transformKeysVals(inputObj, templateObj)
 * 
 * Similar to `transformKeys`, but instead of constructing a new value from the template,
 * it preserves the values from the original `inputObj`. For each key in `inputObj` that matches
 * a pattern in `templateObj`, it uses the transformed key but keeps the original value.
 * Returns a new object with these transformed keys pointing to original values.
 *
 * @param {Object} inputObj - The source object whose keys are matched and values are preserved.
 * @param {Object} templateObj - The object defining patterns and how to transform matching keys.
 * @returns {Object} - A new object with transformed keys but original values.
 */
function transformKeysVals(inputObj, templateObj) {
  const result = {};

  for (const key of Object.keys(inputObj)) {
    for (const [templateKey, templateValue] of Object.entries(templateObj)) {
      // Convert template key to regex pattern
      const regexPattern = templateKey.replace(/#/g, '(\\d+)');
      const regex = new RegExp(`^${regexPattern}$`);

      const match = key.match(regex);
      if (match) {
        const number = match[1]; // Extract number from the match
        // Replace placeholders in template value
        const transformedKey = templateKey.replace(/#/g, number);
        result[transformedKey] = inputObj[transformedKey];
      }
    }
  }

  return result;
}

/**
 * removeLastKey(pattern, obj)
 * 
 * Takes a pattern string with a '#' placeholder and an object.
 * Finds the key that matches the pattern and has the highest digit number, and removes it.
 * Returns a new object without that highest matching key.
 * If no matching key is found, it returns a shallow copy of the original object.
 *
 * @param {string} pattern - The pattern string (e.g. "key#") for matching keys.
 * @param {Object} obj - The object from which the last matching key should be removed.
 * @returns {Object} - A new object without the highest-numbered matching key.
 */
function removeLastKey(pattern, obj) {
  // Create a regex from the pattern by replacing '#' with a capturing group for digits
  const regex = new RegExp("^" + pattern.replace('#', '(\\d+)') + "$");

  let max = -1;
  let keyToRemove = null;

  // Iterate through the keys to find the one with the highest number
  for (const key in obj) {
    const match = key.match(regex);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > max) {
        max = num;
        keyToRemove = key;
      }
    }
  }

  // If no matching key is found, return a shallow copy of the object as is
  if (!keyToRemove) {
    return { ...obj };
  }

  // Create a new object and delete the key with the highest number
  const newObj = { ...obj };
  delete newObj[keyToRemove];
  return newObj;
}

/**
 * removeMatchingKeys(pattern, obj)
 * 
 * Takes a pattern string with a '#' placeholder and an object.
 * Removes all keys from the object that match the pattern (where '#' can be any sequence of digits).
 * Returns a new object that excludes all matching keys.
 *
 * @param {string} pattern - The pattern string (e.g. "key#") for matching keys.
 * @param {Object} obj - The object from which the matching keys should be removed.
 * @returns {Object} - A new object without any keys that match the pattern.
 */
function removeMatchingKeys(pattern, obj) {
  // Create a regex from the pattern by replacing '#' with a pattern that matches one or more digits.
  const regex = new RegExp("^" + pattern.replace('#', '\\d+') + "$");

  // Build a new object including only keys that do not match the regex.
  const newObj = {};
  for (const key in obj) {
    if (!regex.test(key)) {
      newObj[key] = obj[key];
    }
  }
  return newObj;
}



export const SingleChartField = ({ chartManager, keyId }) => {
  const editState = chartManager.editingChart
  // Determine the display type of the current field (e.g., "$metric", "$string", "$dynamic")
  const display = editState.chartType[keyId]

  const label = editState.chartType["_" + keyId]
  let initialState = editState.currentValue[keyId]
  // If display type is dynamic (eg, stacked bar), extract and initalize the relevant state.
  if (display === '$dynamic') {
    initialState = transformKeysVals(editState.currentValue, { [keyId]: label })
  }
  const [selectedValue, setSelectedValue] = useState(initialState);
  let displayValue = selectedValue;

  // Update chartManager whenever the selected value changes
  useEffect(() => {
    if (display === '$dynamic') {
      // Transform current stored state to compare with the selected value
      const currentStoredState = transformKeysVals(editState.currentValue, { [keyId]: label })
      // If selected value differs, update the chartManager with new dynamic state
      // (The check is required to avoid unnecessary updates)
      if (JSON.stringify(selectedValue) !== JSON.stringify(currentStoredState)) {
        const oldDataCleaned = removeMatchingKeys(keyId, editState.currentValue)
        chartManager.updateEditChartDirect({ ...oldDataCleaned, ...selectedValue })
      }
    }
    else if (selectedValue !== editState.currentValue[keyId]) {
      // For non-dynamic fields, update normally if the value changed
      const forceVerUpdate = display === '$metric' ? 1 : 0
      chartManager.updateEditChart(keyId, selectedValue, forceVerUpdate)
    }
  }, [selectedValue])
    // Render UI based on the type of display specified
  switch (display) {
    case '$metric':
      // Map metrics to dropdown-friendly objects
      const metrics = chartManager.fullMetrics.map((metricData) => ({ key: metricData.key, label: metricData.name }))
      // Find the currently selected metric label
      const metric = chartManager.fullMetrics.find(metric => metric.key === displayValue);
      displayValue = metric?.name;
      return (
        <Form.Item label={label} name={keyId} style={{ marginTop: "2px", marginBottom: "2px" }}>
          <Select
            value={displayValue}
            showSearch
            optionFilterProp="value"
            onChange={(newVal) => { setSelectedValue(newVal) }}
            className={`forceplate-stat-comparison`}
            itemx={displayValue}
            defaultValue={displayValue}
            style={{ width: "200px" }}
          >

            {metrics.map((metric) => <Select.Option
              key={metric.key} value={metric.key}>{metric.label}</Select.Option>)}
          </Select>
        </Form.Item>)
    case '$string':
      // Case when the field is a simple text input
      return (
        <Form.Item style={{ textAlign: 'left', marginTop: "2px", marginBottom: "2px" }} label={label} name={keyId}>
          <Input
            onChange={(e) => {
              setSelectedValue(e.target.value)
            }}
            value={displayValue}
            defaultValue={displayValue}
            style={{ width: "200px" }}
          />
        </Form.Item>)
    case '$dynamic':
      // Case when the field is a dynamic list of selectable metrics
      const dynamicMetrics = chartManager.fullMetrics.map((metricData) => ({
        key: metricData.key,
        label: metricData.name,
      }));
      const updateSingleVal = (innerKey) => (newVal) => {
        setSelectedValue((oldState) => ({
          ...oldState,
          [innerKey]: newVal,
        }));
      };

      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <div
            style={{
              flex: 1,
              maxHeight: "200px",
              overflowY: "scroll",
              overflowX: "hidden",
              width: "100%",
              border: "1px solid #ddd",
              padding: "20px",
              marginTop: "10px",
              // margin: "5px",
              borderRadius: "5px",
            }}
          >
            {Object.keys(displayValue).map((innerKey, idx) => {
              const innerMetric = chartManager.fullMetrics.find(
                (metric) => metric.key === displayValue[innerKey]
              );
              const displayValueInner = innerMetric?.name;
              return (
                <Form.Item
                  key={innerKey}
                  style={{ textAlign: "left", marginTop: "2px", marginBottom: "2px" }}
                  label={label.replace("#", (idx + 1).toString())}
                  name={innerKey}
                >
                  <Select
                    value={displayValueInner}
                    showSearch
                    optionFilterProp="value"
                    onChange={updateSingleVal(innerKey)}
                    className="forceplate-stat-comparison"
                    defaultValue={displayValueInner}
                    style={{ width: "180px" }}
                  >
                    {dynamicMetrics.map((metric) => (
                      <Select.Option key={metric.key} value={metric.key}>
                        {metric.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            })}
          </div>
          <div
            style={{
              width: "10px",
              paddingLeft: "5px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-around",
              alignItems: "center",
              margin: "10px",
            }}
          >
            {Object.keys(displayValue).length < 8 && (
              <Button
                style={{ paddingTop: "0px",marginRight:"10px" }}
                onClick={() => {
                  const dynamicObjectWithNewKey = addNextKey(keyId, selectedValue);
                  setSelectedValue(dynamicObjectWithNewKey);
                }}
              >
                <PlusOutlined />
              </Button>
            )}
            {Object.keys(displayValue).length > 2 && (
                <Button
                  style={{ paddingTop: "0px" }}
                  onClick={() => {
                    const dynamicObjectWithRemovedKey = removeLastKey(keyId, selectedValue);
                    setSelectedValue(dynamicObjectWithRemovedKey);
                  }}
                >
                  <MinusOutlined />
                </Button>            )}
          </div>
        </div>
      );
    default:
      if (display.startsWith("$auto")) {
        return null;
      } else {
        return <div>Not supported.</div>
      }
  }
}